// Components/Tabs/NewMachineTab.jsx

import React, { useCallback } from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import SliderFormDropDown from "../NewMachineDropDown/NewMachineDropDown";
import useAdminToast from "../useAdminToast";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * NewMachineTab Component
 * 
 * This component provides a form for creating a new machine within a lab. It handles
 * form submission and confirms completion with a toast.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function NewMachineTab({ user, labId, pushTab, revert }) {

  const toast = useAdminToast();
  useDocumentTitle("Admin: New tool instance – Pinpoint");
  const loadTools = useCallback(() => Backend.getMachineTypes(user.UID, 1000), [user.UID]);
  const loadCategories = useCallback(() => Backend.getToolCategoriesByLab(user.UID, labId, 1000), [user.UID, labId]);
  return (
    <Slider
      name="New tool instance"
      navColor="rgb(70, 200, 90)"
      slide={true}
      index={0} // Starting index for the new machine tab
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
    >
      {/* SliderFormDropDown displays the form with dropdowns for machine type and tool categories */}
      <SliderFormDropDown
        title="Tool"
        fields={[
          {
            key: "name",
            placeholder: "Tool instance name",
            validate: /^[A-Za-z0-9]+(?:\s+[A-Za-z0-9\.)(*&^%$#@!)]+)*$/,
            errorText:
              "Must be at least one word and have no leading or trailing spaces.",
          },
        ]}
        dataFunctionMachineTypes={loadTools}
        dataFunctionToolCategories={loadCategories}
        onSubmit={async (values) => {
          const response = await Backend.newMachine({ ...values, UID: user.UID, lab: labId });
          if (response.status !== 201 && response.status !== 200) throw new Error("create_failed");
          toast({ title: "Tool instance created" });
          revert(1);
        }}
      />
    </Slider>
  );
}

// PropTypes for type-checking component props
NewMachineTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default NewMachineTab;
